import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import {
  EntityType,
  PermissionLevel,
} from '../../../../../generated/data-contracts';
import { HierarchicalFilterSelections } from '../../../../../components/HierarchicalFilter';
import { AddPermissionDialogProps } from '../types';
import { entityConfig } from '../constants/entityConfig';
import { useRolePermissionsApi } from '../hooks/useRolePermissionsApi';
import { useEntityDataLoader } from '../hooks/useEntityDataLoader';
import { EntitySelector } from './EntitySelector';
import { PermissionSelector } from './PermissionSelector';

export const AddPermissionDialog: React.FC<AddPermissionDialogProps> = ({
  open,
  onClose,
  entityType,
  roleId,
  onAdd,
  existingPermissions,
}) => {
  // Estados básicos
  const [permissionLevel, setPermissionLevel] = useState<PermissionLevel>(
    PermissionLevel.READ
  );
  const [selectedEntities, setSelectedEntities] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Estados para filtros jerárquicos
  const [hierarchicalSelections, setHierarchicalSelections] =
    useState<HierarchicalFilterSelections>({});
  const [selectedDashboardGroup, setSelectedDashboardGroup] = useState<
    number | undefined
  >();

  const { api, notify } = useRolePermissionsApi();
  const {
    clients,
    plants,
    dashboardGroups,
    dashboards,
    alerts,
    diagrams,
    loadingClients,
    loadingPlants,
    loadingDashboardGroups,
    loadingDashboards,
    loadingAlerts,
    loadingDiagrams,
    loadClients,
    loadPlants,
    loadDashboardGroups,
    loadDashboardGroupsUnfiltered,
    loadDashboards,
    loadAlerts,
    loadDiagrams,
    resetData,
  } = useEntityDataLoader(existingPermissions);

  // Efecto para cargar datos según el tipo de entidad
  useEffect(() => {
    if (!open) return;

    // Reset states
    setSelectedEntities([]);
    setHierarchicalSelections({});
    setSelectedDashboardGroup(undefined);
    resetData();

    // Cargar datos según el tipo de entidad
    switch (entityType) {
      case EntityType.CLIENT:
        loadClients();
        break;
      case EntityType.PLANT:
        loadPlants();
        break;
      case EntityType.DASHBOARDGROUP:
      case EntityType.DASHBOARD:
      case EntityType.ALERT:
      case EntityType.DIAGRAM:
        // Estos requieren filtros jerárquicos, no cargamos nada inicialmente
        break;
    }
  }, [
    open,
    entityType,
    loadClients,
    loadPlants,
    existingPermissions,
    resetData,
  ]);

  // Efecto para manejar cambios en filtros jerárquicos
  useEffect(() => {
    if (!hierarchicalSelections.plantId) return;

    switch (entityType) {
      case EntityType.DASHBOARDGROUP:
        loadDashboardGroups(hierarchicalSelections.plantId);
        break;
      case EntityType.DASHBOARD:
        // Para dashboards, cargamos TODOS los grupos sin filtrar
        loadDashboardGroupsUnfiltered(hierarchicalSelections.plantId);
        setSelectedEntities([]);
        setSelectedDashboardGroup(undefined);
        break;
      case EntityType.ALERT:
        setSelectedEntities([]);
        break;
      case EntityType.DIAGRAM:
        loadDiagrams(hierarchicalSelections.plantId);
        break;
    }
  }, [
    hierarchicalSelections.plantId,
    entityType,
    loadDashboardGroups,
    loadDashboardGroupsUnfiltered,
    loadDiagrams,
  ]);

  // Efecto para cargar dashboards cuando se selecciona un grupo
  useEffect(() => {
    if (entityType === EntityType.DASHBOARD && selectedDashboardGroup) {
      loadDashboards(selectedDashboardGroup);
    }
  }, [selectedDashboardGroup, entityType, loadDashboards]);

  // Efecto para cargar alertas cuando se selecciona una zona
  useEffect(() => {
    if (entityType === EntityType.ALERT && hierarchicalSelections.zoneId) {
      loadAlerts(hierarchicalSelections.zoneId);
    }
  }, [hierarchicalSelections.zoneId, entityType, loadAlerts]);

  const handleSubmit = async () => {
    if (!selectedEntities || selectedEntities.length === 0) {
      notify('Debe seleccionar al menos una entidad', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      console.log('Adding multiple permissions:', {
        roleId,
        entityType,
        entities: selectedEntities,
        permissionLevel,
      });

      // Crear array de permisos para enviar
      const permissionsToCreate = selectedEntities.map(entity => ({
        roleId: String(roleId),
        entityType,
        entityId: entity.id,
        permissionLevel,
      }));

      console.log('Permissions array to send:', permissionsToCreate);

      // Usar directamente la API para crear múltiples permisos
      await api.rolePermissionsCreate(permissionsToCreate);

      notify(
        `${selectedEntities.length} permiso(s) agregado(s) correctamente`,
        { type: 'success' }
      );
      onAdd(permissionsToCreate);
      handleClose();
    } catch (error) {
      console.error('Error creating role permissions:', error);
      notify('Error al agregar permisos', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedEntities([]);
    setPermissionLevel(PermissionLevel.READ);
    setHierarchicalSelections({});
    setSelectedDashboardGroup(undefined);
    onClose();
  };

  const config = entityConfig[entityType];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='lg' fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: config.bgColor,
              color: config.color,
              width: 56,
              height: 56,
            }}
          >
            {config.icon}
          </Avatar>
          <Box>
            <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
              Agregar Permiso - {config.name}
            </Typography>
            <Typography variant='body2' color='textSecondary'>
              {config.description}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Paper
            sx={{
              p: 3,
              bgcolor: 'background.default',
              border: `1px solid ${config.color}20`,
            }}
          >
            <Typography
              variant='subtitle1'
              gutterBottom
              sx={{ fontWeight: 'bold', mb: 2 }}
            >
              Seleccionar {config.name.slice(0, -1)}
            </Typography>
            <EntitySelector
              entityType={entityType}
              selectedEntities={selectedEntities}
              onEntitiesChange={setSelectedEntities}
              hierarchicalSelections={hierarchicalSelections}
              onHierarchicalChange={setHierarchicalSelections}
              selectedDashboardGroup={selectedDashboardGroup}
              onDashboardGroupChange={setSelectedDashboardGroup}
              clients={clients}
              plants={plants}
              dashboardGroups={dashboardGroups}
              dashboards={dashboards}
              alerts={alerts}
              diagrams={diagrams}
              loadingClients={loadingClients}
              loadingPlants={loadingPlants}
              loadingDashboardGroups={loadingDashboardGroups}
              loadingDashboards={loadingDashboards}
              loadingAlerts={loadingAlerts}
              loadingDiagrams={loadingDiagrams}
            />
          </Paper>

          <Paper
            sx={{
              p: 3,
              bgcolor: 'background.default',
              border: `1px solid ${config.color}20`,
            }}
          >
            <Typography
              variant='subtitle1'
              gutterBottom
              sx={{ fontWeight: 'bold', mb: 2 }}
            >
              Nivel de Permiso
            </Typography>
            <PermissionSelector
              selectedLevel={permissionLevel}
              onLevelChange={setPermissionLevel}
            />
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant='outlined'
          size='large'
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={
            !selectedEntities || selectedEntities.length === 0 || loading
          }
          startIcon={
            loading ? (
              <CircularProgress size={16} color='inherit' />
            ) : (
              <AddIcon />
            )
          }
          size='large'
          sx={{
            bgcolor: config.color,
            minWidth: 150,
            '&:hover': {
              bgcolor: config.color,
              filter: 'brightness(0.9)',
            },
          }}
        >
          {loading
            ? 'Agregando...'
            : `Agregar ${selectedEntities.length} Permiso(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
